import { createBrowserRouter } from 'react-router-dom';
import { adminRouteEntries } from '@modules/admin';
import { backupRouteEntries } from '@modules/backup';
import { catalogRouteEntries } from '@modules/catalog';
import { changeMgmtRouteEntries } from '@modules/change-mgmt';
import { clientsRouteEntries } from '@modules/clients';
import { cloudRouteEntries } from '@modules/cloud';
import { computeRouteEntries } from '@modules/compute';
import { enterpriseRouteEntries } from '@modules/enterprise';
import {
  ChangePasswordPage,
  ForgotPasswordPage,
  LoginPage,
  OtpVerifyPage,
  ResetPasswordPage,
  iamRouteEntries,
} from '@modules/iam';
import { incidentsRouteEntries } from '@modules/incidents';
import { infrastructureRouteEntries } from '@modules/infrastructure';
import { workforceRouteEntries } from '@modules/workforce';
import { FEATURES } from '@shared/config/features';
import ProtectedRoute from '../shell/ProtectedRoute';
import { lazyLoad, lazyLoadWithProps } from './lazyRoute';

const dashboardRoute = () =>
  lazyLoadWithProps(
    [adminRouteEntries.dashboard, backupRouteEntries.backupPoliciesList] as const,
    (dashboardPageModule, backupPoliciesListPageModule) => {
      const DashboardPage = dashboardPageModule.default;
      const BackupPoliciesListPage = backupPoliciesListPageModule.default;
      return <DashboardPage backupPoliciesSlot={<BackupPoliciesListPage embedded />} />;
    }
  );

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/otp', element: <OtpVerifyPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  {
    path: '/change-password',
    element: (
      <ProtectedRoute>
        <ChangePasswordPage />
      </ProtectedRoute>
    ),
  },
  {
    ...lazyLoadWithProps(
      [
        () => import('../shell/MainLayout'),
        adminRouteEntries.notificationBell,
        () => import('../theme/ThemeColorControl'),
      ] as const,
      (mainLayoutModule, notificationBellModule, themeColorControlModule) => {
        const MainLayout = mainLayoutModule.default;
        const NotificationBell = notificationBellModule.default;
        const ThemeColorControl = themeColorControlModule.default;
        return (
          <ProtectedRoute>
            <MainLayout
              notificationSlot={<NotificationBell />}
              themeColorSlot={<ThemeColorControl />}
            />
          </ProtectedRoute>
        );
      }
    ),
    children: [
      { path: '/', ...dashboardRoute() },
      { path: '/dashboard', ...dashboardRoute() },
      { path: '/roles', ...lazyLoad(iamRouteEntries.rolesList) },
      { path: '/roles/new', ...lazyLoad(iamRouteEntries.roleEditor) },
      { path: '/roles/:id', ...lazyLoad(iamRouteEntries.roleEditor) },
      { path: '/clients', ...lazyLoad(clientsRouteEntries.clientsList) },
      { path: '/clients/new', ...lazyLoad(clientsRouteEntries.clientForm) },
      { path: '/clients/:id', ...lazyLoad(clientsRouteEntries.clientDetail) },
      { path: '/clients/:id/edit', ...lazyLoad(clientsRouteEntries.clientForm) },
      ...(FEATURES.OPPORTUNITIES
        ? [
            {
              path: '/clients/:clientId/opportunities/:opportunityId',
              ...lazyLoad(clientsRouteEntries.opportunityDetail),
            },
            {
              path: '/admin/opportunity-step-templates',
              ...lazyLoad(clientsRouteEntries.opportunityTemplatesAdmin),
            },
          ]
        : []),
      {
        path: '/clients/:clientId/datacenters/:dcId/salas',
        ...lazyLoad(clientsRouteEntries.clientSalas),
      },
      {
        path: '/clients/:clientId/salas',
        ...lazyLoad(clientsRouteEntries.clientSalas),
      },
      {
        path: '/clients/:clientId/salas/:salaId/racks',
        ...lazyLoad(clientsRouteEntries.clientRacks),
      },
      {
        path: '/clients/:clientId/racks/:rackId/devices',
        ...lazyLoad(computeRouteEntries.clientDeviceInstances),
      },
      {
        path: '/clients/:clientId/racks/:rackId/device-instances/:instanceId',
        ...lazyLoad(computeRouteEntries.deviceInstanceDetail),
      },
      {
        path: '/clients/:clientId/device-instances/:instanceId',
        ...lazyLoad(computeRouteEntries.deviceInstanceDetail),
      },
      {
        path: '/clients/:clientId/device-instances/:instanceId/vms',
        ...lazyLoad(computeRouteEntries.vmsList),
      },
      {
        path: '/clients/:clientId/device-instances/:instanceId/vms/new',
        ...lazyLoad(computeRouteEntries.vmForm),
      },
      {
        path: '/clients/:clientId/device-instances/:instanceId/vms/:vmId',
        ...lazyLoad(computeRouteEntries.vmDetail),
      },
      {
        path: '/clients/:clientId/clusters',
        ...lazyLoad(computeRouteEntries.vmClustersList),
      },
      {
        path: '/clients/:clientId/clusters/new',
        ...lazyLoad(computeRouteEntries.vmClusterForm),
      },
      {
        path: '/clients/:clientId/clusters/:clusterId',
        ...lazyLoad(computeRouteEntries.vmClusterDetail),
      },
      {
        path: '/clients/:clientId/clusters/:clusterId/edit',
        ...lazyLoad(computeRouteEntries.vmClusterForm),
      },
      { path: '/users', ...lazyLoad(iamRouteEntries.usersList) },
      { path: '/users/new', ...lazyLoad(iamRouteEntries.userCreate) },
      { path: '/users/:id', ...lazyLoad(iamRouteEntries.userDetail) },
      { path: '/users/:id/edit', ...lazyLoad(iamRouteEntries.userEdit) },
      {
        path: '/standby',
        ...lazyLoadWithProps(
          [workforceRouteEntries.standby, incidentsRouteEntries.shiftManagementTab] as const,
          (standbyPageModule, shiftManagementTabModule) => {
            const StandbyPage = standbyPageModule.default;
            const ShiftManagementTab = shiftManagementTabModule.default;
            return <StandbyPage managementTab={<ShiftManagementTab />} />;
          }
        ),
      },
      { path: '/profile', ...lazyLoad(iamRouteEntries.profile) },
      {
        path: '/datacenters',
        ...lazyLoad(infrastructureRouteEntries.datacentersList),
      },
      {
        path: '/datacenters/new',
        ...lazyLoad(infrastructureRouteEntries.datacenterForm),
      },
      {
        path: '/datacenters/:id',
        ...lazyLoad(infrastructureRouteEntries.datacenterDetail),
      },
      {
        path: '/datacenters/:id/edit',
        ...lazyLoad(infrastructureRouteEntries.datacenterForm),
      },
      { path: '/salas', ...lazyLoad(infrastructureRouteEntries.salasList) },
      { path: '/salas/new', ...lazyLoad(infrastructureRouteEntries.salaForm) },
      { path: '/salas/:id', ...lazyLoad(infrastructureRouteEntries.salaDetail) },
      { path: '/salas/:id/edit', ...lazyLoad(infrastructureRouteEntries.salaForm) },
      { path: '/racks', ...lazyLoad(infrastructureRouteEntries.racksList) },
      { path: '/racks/new', ...lazyLoad(infrastructureRouteEntries.rackForm) },
      { path: '/racks/:id', ...lazyLoad(infrastructureRouteEntries.rackDetail) },
      { path: '/racks/:id/edit', ...lazyLoad(infrastructureRouteEntries.rackForm) },
      { path: '/devices', ...lazyLoad(computeRouteEntries.devicesList) },
      { path: '/devices/new', ...lazyLoad(computeRouteEntries.deviceForm) },
      { path: '/devices/:id', ...lazyLoad(computeRouteEntries.deviceDetail) },
      { path: '/devices/:id/edit', ...lazyLoad(computeRouteEntries.deviceForm) },
      { path: '/platforms', ...lazyLoad(catalogRouteEntries.platformsList) },
      { path: '/platforms/new', ...lazyLoad(catalogRouteEntries.platformForm) },
      { path: '/platforms/:id/edit', ...lazyLoad(catalogRouteEntries.platformForm) },
      {
        path: '/operating-systems',
        ...lazyLoad(catalogRouteEntries.operatingSystemsList),
      },
      {
        path: '/operating-systems/new',
        ...lazyLoad(catalogRouteEntries.operatingSystemForm),
      },
      {
        path: '/operating-systems/:id/edit',
        ...lazyLoad(catalogRouteEntries.operatingSystemForm),
      },
      {
        path: '/database-systems',
        ...lazyLoad(catalogRouteEntries.databaseSystemsList),
      },
      {
        path: '/database-systems/new',
        ...lazyLoad(catalogRouteEntries.databaseSystemForm),
      },
      {
        path: '/database-systems/:id/edit',
        ...lazyLoad(catalogRouteEntries.databaseSystemForm),
      },
      { path: '/sap-products', ...lazyLoad(catalogRouteEntries.sapProductsList) },
      { path: '/sap-products/new', ...lazyLoad(catalogRouteEntries.sapProductForm) },
      {
        path: '/sap-products/:id/edit',
        ...lazyLoad(catalogRouteEntries.sapProductForm),
      },
      {
        path: '/subsystems-catalog',
        ...lazyLoad(catalogRouteEntries.subsystemsList),
      },
      {
        path: '/subsystems-catalog/new',
        ...lazyLoad(catalogRouteEntries.subsystemForm),
      },
      {
        path: '/subsystems-catalog/:id/edit',
        ...lazyLoad(catalogRouteEntries.subsystemForm),
      },
      {
        path: '/enterprise',
        ...lazyLoad(enterpriseRouteEntries.enterpriseAdmin),
      },
      {
        path: '/scheduled-activities/new',
        ...lazyLoad(changeMgmtRouteEntries.changeForm),
      },
      {
        path: '/scheduled-activities/:id',
        ...lazyLoad(changeMgmtRouteEntries.changeDetail),
      },
      {
        path: '/scheduled-activities/:id/edit',
        ...lazyLoad(changeMgmtRouteEntries.changeForm),
      },
      {
        path: '/scheduled-activities/:id/execute',
        ...lazyLoad(changeMgmtRouteEntries.executionTracker),
      },
      { path: '/templates', ...lazyLoad(changeMgmtRouteEntries.templatesList) },
      { path: '/templates/new', ...lazyLoad(changeMgmtRouteEntries.templateForm) },
      { path: '/templates/:id', ...lazyLoad(changeMgmtRouteEntries.templateDetail) },
      { path: '/batches', ...lazyLoad(changeMgmtRouteEntries.batchesList) },
      { path: '/batches/new', ...lazyLoad(changeMgmtRouteEntries.batchCreate) },
      { path: '/batches/:id', ...lazyLoad(changeMgmtRouteEntries.batchDetail) },
      { path: '/cloud-accounts', ...lazyLoad(cloudRouteEntries.cloudAccountsList) },
      { path: '/cloud-accounts/new', ...lazyLoad(cloudRouteEntries.cloudAccountForm) },
      {
        path: '/cloud-accounts/:id',
        ...lazyLoad(cloudRouteEntries.cloudAccountDetail),
      },
      {
        path: '/cloud-accounts/:id/edit',
        ...lazyLoad(cloudRouteEntries.cloudAccountForm),
      },
      { path: '/cloud-volumes', ...lazyLoad(cloudRouteEntries.cloudVolumesList) },
      { path: '/cloud-volumes/new', ...lazyLoad(cloudRouteEntries.cloudVolumeForm) },
      {
        path: '/cloud-volumes/:id',
        ...lazyLoad(cloudRouteEntries.cloudVolumeDetail),
      },
      {
        path: '/cloud-volumes/:id/edit',
        ...lazyLoad(cloudRouteEntries.cloudVolumeForm),
      },
      { path: '/cloud-vms/new', ...lazyLoad(cloudRouteEntries.cloudVmForm) },
      { path: '/cloud-vms/:vmId', ...lazyLoad(cloudRouteEntries.cloudVmDetail) },
      { path: '/cloud-vms/:vmId/edit', ...lazyLoad(cloudRouteEntries.cloudVmForm) },
      { path: '/cloud-services', ...lazyLoad(cloudRouteEntries.cloudServicesList) },
      { path: '/cloud-services/new', ...lazyLoad(cloudRouteEntries.cloudServiceForm) },
      {
        path: '/cloud-services/:id',
        ...lazyLoad(cloudRouteEntries.cloudServiceDetail),
      },
      {
        path: '/cloud-services/:id/edit',
        ...lazyLoad(cloudRouteEntries.cloudServiceForm),
      },
      { path: '/overtime', ...lazyLoad(workforceRouteEntries.overtimeAdmin) },
      { path: '/incidents', ...lazyLoad(incidentsRouteEntries.incidents) },
      { path: '/admin', ...lazyLoad(adminRouteEntries.admin) },
    ],
  },
]);

export default router;